import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, DatePicker, Form, InputNumber, Modal, Table, Tag, Tooltip, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import React, { useMemo, useState } from "react";

import { type SalesCommissionConfig, type TeamMember } from "@fitness-app/data-models/entities/TeamMember";

import { ColumnVisibilityMenu } from "~/components/ColumnVisibilityMenu/ColumnVisibilityMenu";
import { useColumnVisibility, type ToggleableColumn } from "~/hooks/useColumnVisibility";
import { supabase, useAppSelector } from "~/store/initializeStore";

export const DEFAULT_COMMISSION_CONFIG: Required<SalesCommissionConfig> = {
  installmentsPercent: 5,
  oneTimePercent: 7,
  baseAmount: 550_000,
  bikiniSubscriptionPercent: 50,
  bikiniFlatAmount: 5_000,
};

type SettlementRpcRow = {
  teamMemberId: string | null;
  sellerName: string | null;
  wonCount: number;
  paidTotal: number;
  oneTimeTotal: number;
  installmentsTotal: number;
  /** Wpłaty warmupowe — prezentowane przy kontrakcie, poza bazą prowizji (TRE-1527). */
  warmupTotal: number;
  bikiniSubscriptionTotal: number;
  bikiniOneTimeCount: number;
  bikiniOneTimeTotal: number;
  postWarmupWithdrawalCount: number;
};

type CorrectionRpcRow = {
  teamMemberId: string | null;
  sellerName: string | null;
  refundedCount: number;
  warmupCancelledCount: number;
  oneTimeTotal: number;
  installmentsTotal: number;
  bikiniSubscriptionTotal: number;
  bikiniOneTimeCount: number;
  bikiniOneTimeTotal: number;
};

type SettlementRow = SettlementRpcRow & {
  config: Required<SalesCommissionConfig>;
  oneTimeCommission: number;
  installmentsCommission: number;
  bikiniCommission: number;
  refundedCount: number;
  /** Ile z korekt to anulowania w trakcie warmupu, a nie zwroty (TRE-1527). */
  warmupCancelledCount: number;
  // Whole commission clawed back for refunds booked this month (TRE-1526).
  refundedCommission: number;
  payout: number;
};

type CommissionBuckets = Pick<
  SettlementRpcRow,
  "oneTimeTotal" | "installmentsTotal" | "bikiniSubscriptionTotal" | "bikiniOneTimeCount"
>;

const computeCommission = (totals: CommissionBuckets, config: Required<SalesCommissionConfig>) => {
  const oneTime = Math.round((totals.oneTimeTotal * config.oneTimePercent) / 100);
  const installments = Math.round((totals.installmentsTotal * config.installmentsPercent) / 100);
  // Bikini: subscription pays a percent of the first payment (also after a
  // discount), one-time bikini pays a flat amount per sale.
  const bikini =
    Math.round((totals.bikiniSubscriptionTotal * config.bikiniSubscriptionPercent) / 100) +
    totals.bikiniOneTimeCount * config.bikiniFlatAmount;

  return { oneTime, installments, bikini, total: oneTime + installments + bikini };
};

type CommissionForm = {
  installmentsPercent: number;
  oneTimePercent: number;
  baseAmount: number;
  bikiniSubscriptionPercent: number;
  bikiniFlatAmount: number;
};

type SettlementDetailRow = {
  teamMemberId: string | null;
  customerBundleId: string;
  purchasedAt: string;
  clientName: string | null;
  clientEmail: string;
  packageName: string;
  bucket: "one_time" | "installments" | "bikini_subscription" | "bikini_one_time";
  paidAmount: number;
  /** Wpłata, od której liczy się prowizja — przy warmupie pierwsza REGULARNA rata. */
  commissionPaidAmount: number;
  contractedAmount: number;
  warmupAmount: number | null;
  oneTimeDiscountAmount: number | null;
  postWarmupWithdrawalAt: string | null;
  cycles: number | null;
};

type CorrectionReason = "refund" | "warmup_cancellation";

type CorrectionDetailRow = {
  teamMemberId: string | null;
  customerBundleId: string;
  refundedAt: string;
  purchasedAt: string;
  clientName: string | null;
  clientEmail: string;
  packageName: string;
  bucket: SettlementDetailRow["bucket"];
  reason: CorrectionReason | string;
  paidAmount: number;
  contractedAmount: number;
  cycles: number | null;
  refundAmount: number | null;
};

type SettlementColumnKey =
  | "wonCount"
  | "oneTime"
  | "installments"
  | "bikini"
  | "oneTimeCommission"
  | "installmentsCommission"
  | "bikiniCommission"
  | "refunded"
  | "base"
  | "payout";

// "Sprzedawca" stays always visible, so the table never ends up empty (TRE-2398).
const settlementColumns: ToggleableColumn<SettlementColumnKey>[] = [
  { key: "wonCount", label: "Wygrane" },
  { key: "oneTime", label: "Jednorazowe" },
  { key: "installments", label: "Raty zakontraktowane" },
  { key: "bikini", label: "Bikini" },
  { key: "oneTimeCommission", label: "Prowizja jednorazowe" },
  { key: "installmentsCommission", label: "Prowizja raty" },
  { key: "bikiniCommission", label: "Prowizja bikini" },
  { key: "refunded", label: "Korekta (zwroty)" },
  { key: "base", label: "Podstawa" },
  { key: "payout", label: "Do wypłaty" },
];

const correctionReasonLabels: Record<CorrectionReason, { label: string; color: string }> = {
  refund: { label: "Zwrot do 30 dni", color: "red" },
  warmup_cancellation: { label: "Anulowanie w warmupie", color: "volcano" },
};

const bucketLabels: Record<SettlementDetailRow["bucket"], { label: string; color: string }> = {
  one_time: { label: "Jednorazowy", color: "blue" },
  installments: { label: "Raty", color: "purple" },
  bikini_subscription: { label: "Bikini subskrypcja", color: "magenta" },
  bikini_one_time: { label: "Bikini jednorazowy", color: "pink" },
};

const formatMoney = (value: number | null | undefined) =>
  typeof value === "number"
    ? new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", minimumFractionDigits: 2 }).format(
        value / 100,
      )
    : "-";

const resolveCommissionConfig = (member: TeamMember | undefined): Required<SalesCommissionConfig> => ({
  installmentsPercent:
    member?.salesCommissionConfig?.installmentsPercent ?? DEFAULT_COMMISSION_CONFIG.installmentsPercent,
  oneTimePercent: member?.salesCommissionConfig?.oneTimePercent ?? DEFAULT_COMMISSION_CONFIG.oneTimePercent,
  baseAmount: member?.salesCommissionConfig?.baseAmount ?? DEFAULT_COMMISSION_CONFIG.baseAmount,
  bikiniSubscriptionPercent:
    member?.salesCommissionConfig?.bikiniSubscriptionPercent ?? DEFAULT_COMMISSION_CONFIG.bikiniSubscriptionPercent,
  bikiniFlatAmount: member?.salesCommissionConfig?.bikiniFlatAmount ?? DEFAULT_COMMISSION_CONFIG.bikiniFlatAmount,
});

interface Props {
  canManageConfiguration: boolean;
  /** The caller reads their own settlement row only — no seller picker, no total. */
  isOwnRowOnly: boolean;
  sellers: TeamMember[];
  onConfigSaved: () => void;
}

export const SalesSettlements = ({
  canManageConfiguration,
  isOwnRowOnly,
  sellers,
  onConfigSaved,
}: Props): React.ReactElement => {
  const queryClient = useQueryClient();
  // These RPCs answer differently per caller — a tenant-wide reader gets every
  // seller, a seller gets only themselves — so the month alone does not
  // identify a result. The QueryClient lives as long as the tab, so without the
  // caller in the key a re-login on a shared machine can render the previous
  // user's numbers straight from cache (TRE-1827).
  const userId = useAppSelector((state) => state.user.data?.id);
  const callerScope = [userId, isOwnRowOnly ? "self" : "all"];
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf("month"));
  const [configMember, setConfigMember] = useState<TeamMember | null>(null);
  const [form] = Form.useForm<CommissionForm>();
  const { hiddenColumns, isColumnVisible, setHiddenColumns } = useColumnVisibility("salesCrmSettlements");

  const from = month.startOf("month");
  const to = from.add(1, "month");

  const settlementQuery = useQuery({
    queryKey: ["salesCrmSettlement", ...callerScope, from.toISOString()],
    retry: false,
    keepPreviousData: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("sales_crm_monthly_settlement", {
        p_from: from.toISOString(),
        p_to: to.toISOString(),
      });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as SettlementRpcRow[];
    },
  });

  const detailsQuery = useQuery({
    queryKey: ["salesCrmSettlementDetails", ...callerScope, from.toISOString()],
    retry: false,
    keepPreviousData: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("sales_crm_monthly_settlement_details", {
        p_from: from.toISOString(),
        p_to: to.toISOString(),
      });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as SettlementDetailRow[];
    },
  });

  const correctionsQuery = useQuery({
    queryKey: ["salesCrmSettlementCorrections", ...callerScope, from.toISOString()],
    retry: false,
    keepPreviousData: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("sales_crm_monthly_corrections", {
        p_from: from.toISOString(),
        p_to: to.toISOString(),
      });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CorrectionRpcRow[];
    },
  });

  const correctionDetailsQuery = useQuery({
    queryKey: ["salesCrmSettlementCorrectionDetails", ...callerScope, from.toISOString()],
    retry: false,
    keepPreviousData: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("sales_crm_monthly_correction_details", {
        p_from: from.toISOString(),
        p_to: to.toISOString(),
      });

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CorrectionDetailRow[];
    },
  });

  const detailsBySeller = useMemo(() => {
    const grouped = new Map<string, SettlementDetailRow[]>();

    for (const detail of detailsQuery.data ?? []) {
      const key = detail.teamMemberId ?? "unknown";
      const bucket = grouped.get(key);

      if (bucket) {
        bucket.push(detail);
      } else {
        grouped.set(key, [detail]);
      }
    }

    return grouped;
  }, [detailsQuery.data]);

  const sellersMap = useMemo(() => new Map(sellers.map((seller) => [seller.id, seller])), [sellers]);

  const correctionDetailsBySeller = useMemo(() => {
    const grouped = new Map<string, CorrectionDetailRow[]>();

    for (const detail of correctionDetailsQuery.data ?? []) {
      const key = detail.teamMemberId ?? "unknown";
      const bucket = grouped.get(key);

      if (bucket) {
        bucket.push(detail);
      } else {
        grouped.set(key, [detail]);
      }
    }

    return grouped;
  }, [correctionDetailsQuery.data]);

  const saveConfigMutation = useMutation({
    mutationFn: async (values: CommissionForm) => {
      if (!configMember) return;

      const { error } = await supabase.rpc("sales_crm_set_commission_config", {
        p_team_member_id: configMember.id,
        p_config: {
          installmentsPercent: values.installmentsPercent,
          oneTimePercent: values.oneTimePercent,
          baseAmount: Math.round(values.baseAmount * 100),
          bikiniSubscriptionPercent: values.bikiniSubscriptionPercent,
          bikiniFlatAmount: Math.round(values.bikiniFlatAmount * 100),
        },
      });

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setConfigMember(null);
      form.resetFields();
      onConfigSaved();
      await queryClient.invalidateQueries({ queryKey: ["salesCrmSettlement"] });
      void message.success("Stawki zapisane");
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const rows = useMemo<SettlementRow[]>(() => {
    const emptySettlement = (teamMemberId: string | null, sellerName: string | null): SettlementRpcRow => ({
      teamMemberId,
      sellerName,
      wonCount: 0,
      paidTotal: 0,
      oneTimeTotal: 0,
      installmentsTotal: 0,
      warmupTotal: 0,
      bikiniSubscriptionTotal: 0,
      bikiniOneTimeCount: 0,
      bikiniOneTimeTotal: 0,
      postWarmupWithdrawalCount: 0,
    });

    // A refund correction lands in the month it was refunded, which can differ
    // from any sale in that month — merge both sources so a seller with only a
    // clawback still shows up (base minus the reversed commission).
    const merged = new Map<string, { base: SettlementRpcRow; correction: CorrectionRpcRow | null }>();

    for (const row of settlementQuery.data ?? []) {
      merged.set(row.teamMemberId ?? "unknown", { base: row, correction: null });
    }

    for (const correction of correctionsQuery.data ?? []) {
      const key = correction.teamMemberId ?? "unknown";
      const existing = merged.get(key);

      if (existing) {
        existing.correction = correction;
      } else {
        merged.set(key, {
          base: emptySettlement(correction.teamMemberId, correction.sellerName),
          correction,
        });
      }
    }

    return Array.from(merged.values()).map(({ base: row, correction }) => {
      const member = row.teamMemberId ? sellersMap.get(row.teamMemberId) : undefined;
      const config = resolveCommissionConfig(member);
      const commission = computeCommission(row, config);
      const refunded = correction
        ? computeCommission(correction, config)
        : { oneTime: 0, installments: 0, bikini: 0, total: 0 };

      return {
        ...row,
        sellerName: row.sellerName ?? correction?.sellerName ?? null,
        config,
        oneTimeCommission: commission.oneTime,
        installmentsCommission: commission.installments,
        bikiniCommission: commission.bikini,
        refundedCount: correction?.refundedCount ?? 0,
        warmupCancelledCount: correction?.warmupCancelledCount ?? 0,
        refundedCommission: refunded.total,
        payout: config.baseAmount + commission.total - refunded.total,
      };
    });
  }, [settlementQuery.data, correctionsQuery.data, sellersMap]);

  const openConfig = (row: SettlementRow) => {
    const member = row.teamMemberId ? sellersMap.get(row.teamMemberId) : undefined;

    if (!member) {
      void message.warning("Nie znaleziono sprzedawcy w zespole");
      return;
    }

    setConfigMember(member);
    form.setFieldsValue({
      installmentsPercent: row.config.installmentsPercent,
      oneTimePercent: row.config.oneTimePercent,
      baseAmount: row.config.baseAmount / 100,
      bikiniSubscriptionPercent: row.config.bikiniSubscriptionPercent,
      bikiniFlatAmount: row.config.bikiniFlatAmount / 100,
    });
  };

  return (
    <Card
      loading={settlementQuery.isInitialLoading}
      styles={{ body: { padding: 20 } }}
      className="rounded-lg border border-gray-200 shadow-sm"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Rozliczenia
          </Typography.Text>
          <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg !font-semibold">
            {isOwnRowOnly ? "Twoje miesięczne rozliczenie" : "Miesięczne rozliczenie sprzedawców"}
          </Typography.Title>
          <Typography.Text className="text-gray-500">
            Liczone po dacie sprzedaży (data zakupu pakietu), z potwierdzonych sprzedaży. Zwrot do 30 dni od zakupu cofa
            całą prowizję jako korekta w miesiącu zwrotu.
          </Typography.Text>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ColumnVisibilityMenu columns={settlementColumns} hiddenColumns={hiddenColumns} onChange={setHiddenColumns} />
          <DatePicker
            picker="month"
            allowClear={false}
            value={month}
            format="MM.YYYY"
            onChange={(value) => value && setMonth(value.startOf("month"))}
          />
        </div>
      </div>

      <Table<SettlementRow>
        rowKey={(row) => row.teamMemberId ?? row.sellerName ?? "unknown"}
        dataSource={rows}
        loading={settlementQuery.isFetching || correctionsQuery.isFetching}
        pagination={false}
        scroll={{ x: true }}
        expandable={{
          rowExpandable: (row) =>
            (detailsBySeller.get(row.teamMemberId ?? "unknown") ?? []).length > 0 ||
            (correctionDetailsBySeller.get(row.teamMemberId ?? "unknown") ?? []).length > 0,
          expandedRowRender: (row) => {
            const salesDetails = detailsBySeller.get(row.teamMemberId ?? "unknown") ?? [];
            const correctionDetails = correctionDetailsBySeller.get(row.teamMemberId ?? "unknown") ?? [];

            return (
              <div className="flex flex-col gap-4">
                {salesDetails.length > 0 ? (
                  <Table<SettlementDetailRow>
                    rowKey="customerBundleId"
                    dataSource={salesDetails}
                    loading={detailsQuery.isFetching}
                    pagination={false}
                    size="small"
                  >
                    <Table.Column<SettlementDetailRow>
                      title="Data zakupu"
                      render={(_, detail) => dayjs(detail.purchasedAt).format("DD.MM.YYYY HH:mm")}
                    />
                    <Table.Column<SettlementDetailRow>
                      title="Klient"
                      render={(_, detail) => (
                        <div className="min-w-0">
                          <div>{detail.clientName ?? "-"}</div>
                          <Typography.Text type="secondary" className="text-xs">
                            {detail.clientEmail}
                          </Typography.Text>
                        </div>
                      )}
                    />
                    <Table.Column<SettlementDetailRow> title="Pakiet" dataIndex="packageName" />
                    <Table.Column<SettlementDetailRow>
                      title="Typ"
                      render={(_, detail) => (
                        <div className="flex flex-wrap gap-1">
                          <Tag color={bucketLabels[detail.bucket]?.color}>
                            {bucketLabels[detail.bucket]?.label ?? detail.bucket}
                            {detail.bucket === "installments" && detail.cycles ? ` (${detail.cycles})` : ""}
                          </Tag>
                          {detail.warmupAmount ? (
                            <Tooltip
                              title={`Wpłata warmupowa ${formatMoney(
                                detail.warmupAmount,
                              )} — nie wchodzi do podstawy prowizji`}
                            >
                              <Tag color="gold">Warmup {formatMoney(detail.warmupAmount)}</Tag>
                            </Tooltip>
                          ) : null}
                          {detail.postWarmupWithdrawalAt ? (
                            <Tooltip
                              title={`Wycofanie ${dayjs(detail.postWarmupWithdrawalAt).format(
                                "DD.MM.YYYY",
                              )} — po warmupie, prowizja zostaje`}
                            >
                              <Tag color="orange">Wycofany po warmupie</Tag>
                            </Tooltip>
                          ) : null}
                        </div>
                      )}
                    />
                    <Table.Column<SettlementDetailRow>
                      title="Pierwsza wpłata"
                      render={(_, detail) =>
                        detail.commissionPaidAmount !== detail.paidAmount ? (
                          <Tooltip
                            title={`Prowizja liczona od pierwszej regularnej wpłaty: ${formatMoney(
                              detail.commissionPaidAmount,
                            )}`}
                          >
                            {formatMoney(detail.paidAmount)}
                          </Tooltip>
                        ) : (
                          formatMoney(detail.paidAmount)
                        )
                      }
                    />
                    <Table.Column<SettlementDetailRow>
                      title="Zakontraktowane"
                      render={(_, detail) => (
                        // Bez warmupu i bez rabatu nie ma czego tłumaczyć — `undefined`
                        // zamiast pustego fragmentu, żeby antd nie pokazywał pustej dymki.
                        <Tooltip
                          title={
                            detail.warmupAmount || detail.oneTimeDiscountAmount ? (
                              <>
                                {detail.warmupAmount ? (
                                  <div>
                                    Kontrakt {formatMoney(detail.contractedAmount)} + warmup{" "}
                                    {formatMoney(detail.warmupAmount)}; prowizja od kontraktu
                                  </div>
                                ) : null}
                                {detail.oneTimeDiscountAmount ? (
                                  <div>
                                    Rabat jednorazowy na 1. ratę: {formatMoney(detail.oneTimeDiscountAmount)} (poza
                                    kontraktem)
                                  </div>
                                ) : null}
                              </>
                            ) : undefined
                          }
                        >
                          {formatMoney(detail.contractedAmount + (detail.warmupAmount ?? 0))}
                        </Tooltip>
                      )}
                    />
                  </Table>
                ) : null}

                {correctionDetails.length > 0 ? (
                  <div>
                    <Typography.Text className="text-xs font-medium uppercase tracking-wide text-red-500">
                      Korekty prowizji — zwroty do 30 dni i anulowania w warmupie
                    </Typography.Text>
                    <Table<CorrectionDetailRow>
                      rowKey="customerBundleId"
                      dataSource={correctionDetails}
                      loading={correctionDetailsQuery.isFetching}
                      pagination={false}
                      size="small"
                      className="mt-1"
                    >
                      <Table.Column<CorrectionDetailRow>
                        title="Data korekty"
                        render={(_, detail) => dayjs(detail.refundedAt).format("DD.MM.YYYY HH:mm")}
                      />
                      <Table.Column<CorrectionDetailRow>
                        title="Powód"
                        render={(_, detail) => {
                          const reason = correctionReasonLabels[detail.reason as CorrectionReason];

                          return <Tag color={reason?.color ?? "red"}>{reason?.label ?? detail.reason}</Tag>;
                        }}
                      />
                      <Table.Column<CorrectionDetailRow>
                        title="Data zakupu"
                        render={(_, detail) => dayjs(detail.purchasedAt).format("DD.MM.YYYY")}
                      />
                      <Table.Column<CorrectionDetailRow>
                        title="Klient"
                        render={(_, detail) => (
                          <div className="min-w-0">
                            <div>{detail.clientName ?? "-"}</div>
                            <Typography.Text type="secondary" className="text-xs">
                              {detail.clientEmail}
                            </Typography.Text>
                          </div>
                        )}
                      />
                      <Table.Column<CorrectionDetailRow> title="Pakiet" dataIndex="packageName" />
                      <Table.Column<CorrectionDetailRow>
                        title="Typ"
                        render={(_, detail) => (
                          <Tag color={bucketLabels[detail.bucket]?.color}>
                            {bucketLabels[detail.bucket]?.label ?? detail.bucket}
                            {detail.bucket === "installments" && detail.cycles ? ` (${detail.cycles})` : ""}
                          </Tag>
                        )}
                      />
                      <Table.Column<CorrectionDetailRow>
                        title="Cofnięta prowizja od"
                        render={(_, detail) => {
                          const member = detail.teamMemberId ? sellersMap.get(detail.teamMemberId) : undefined;
                          const config = resolveCommissionConfig(member);
                          const base =
                            detail.bucket === "bikini_subscription" ? detail.paidAmount : detail.contractedAmount;
                          const reversed = computeCommission(
                            {
                              oneTimeTotal: detail.bucket === "one_time" ? detail.contractedAmount : 0,
                              installmentsTotal: detail.bucket === "installments" ? detail.contractedAmount : 0,
                              bikiniSubscriptionTotal: detail.bucket === "bikini_subscription" ? detail.paidAmount : 0,
                              bikiniOneTimeCount: detail.bucket === "bikini_one_time" ? 1 : 0,
                            },
                            config,
                          );

                          return (
                            <Tooltip title={`Podstawa prowizji: ${formatMoney(base)}`}>
                              <Typography.Text type="danger">-{formatMoney(reversed.total)}</Typography.Text>
                            </Tooltip>
                          );
                        }}
                      />
                    </Table>
                  </div>
                ) : null}
              </div>
            );
          },
        }}
        summary={(pageData) => {
          const totals = pageData.reduce(
            (acc, row) => ({
              won: acc.won + row.wonCount,
              oneTime: acc.oneTime + row.oneTimeTotal,
              // Kolumna pokazuje kontrakt razem z warmupem — suma musi się zgadzać.
              installments: acc.installments + row.installmentsTotal + row.warmupTotal,
              bikini: acc.bikini + row.bikiniSubscriptionTotal + row.bikiniOneTimeTotal,
              oneTimeCommission: acc.oneTimeCommission + row.oneTimeCommission,
              installmentsCommission: acc.installmentsCommission + row.installmentsCommission,
              bikiniCommission: acc.bikiniCommission + row.bikiniCommission,
              refundedCommission: acc.refundedCommission + row.refundedCommission,
              base: acc.base + row.config.baseAmount,
              payout: acc.payout + row.payout,
            }),
            {
              won: 0,
              oneTime: 0,
              installments: 0,
              bikini: 0,
              oneTimeCommission: 0,
              installmentsCommission: 0,
              bikiniCommission: 0,
              refundedCommission: 0,
              base: 0,
              payout: 0,
            },
          );

          // A seller sees a single row, so a "Razem" line would just repeat it.
          if (!pageData.length || isOwnRowOnly) return null;

          const summaryCells: Record<SettlementColumnKey, React.ReactNode> = {
            wonCount: totals.won,
            oneTime: formatMoney(totals.oneTime),
            installments: formatMoney(totals.installments),
            bikini: formatMoney(totals.bikini),
            oneTimeCommission: formatMoney(totals.oneTimeCommission),
            installmentsCommission: formatMoney(totals.installmentsCommission),
            bikiniCommission: formatMoney(totals.bikiniCommission),
            refunded: totals.refundedCommission ? (
              <Typography.Text type="danger">-{formatMoney(totals.refundedCommission)}</Typography.Text>
            ) : (
              formatMoney(0)
            ),
            base: formatMoney(totals.base),
            payout: formatMoney(totals.payout),
          };
          const visibleSummaryKeys = settlementColumns.map(({ key }) => key).filter(isColumnVisible);

          return (
            <Table.Summary.Row className="font-semibold">
              {/* expand-icon column */}
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1}>Razem</Table.Summary.Cell>
              {visibleSummaryKeys.map((key, position) => (
                <Table.Summary.Cell key={key} index={position + 2}>
                  {summaryCells[key]}
                </Table.Summary.Cell>
              ))}
              {canManageConfiguration ? <Table.Summary.Cell index={visibleSummaryKeys.length + 2} /> : null}
            </Table.Summary.Row>
          );
        }}
      >
        <Table.Column<SettlementRow>
          title="Sprzedawca"
          render={(_, row) => (
            <div className="flex min-w-0 flex-col gap-1">
              <span>{row.sellerName ?? "-"}</span>
              {row.postWarmupWithdrawalCount ? (
                <Tooltip title="Klient wycofał się po zakończeniu warmupu — prowizja zostaje">
                  <Tag color="orange" className="w-fit">
                    {row.postWarmupWithdrawalCount} × wycofanie po warmupie
                  </Tag>
                </Tooltip>
              ) : null}
            </div>
          )}
        />
        <Table.Column<SettlementRow> title="Wygrane" dataIndex="wonCount" hidden={!isColumnVisible("wonCount")} />
        <Table.Column<SettlementRow>
          title="Jednorazowe"
          hidden={!isColumnVisible("oneTime")}
          render={(_, row) => formatMoney(row.oneTimeTotal)}
        />
        <Table.Column<SettlementRow>
          title="Raty zakontraktowane"
          hidden={!isColumnVisible("installments")}
          render={(_, row) => (
            // Klient płaci kontrakt + warmup, ale prowizja idzie tylko od kontraktu (TRE-1527).
            <Tooltip
              title={
                <>
                  <div>Opłacone dotychczas (pierwsze wpłaty): {formatMoney(row.paidTotal)}</div>
                  {row.warmupTotal ? (
                    <div>
                      W tym warmup {formatMoney(row.warmupTotal)} — poza prowizją (podstawa:{" "}
                      {formatMoney(row.installmentsTotal)})
                    </div>
                  ) : null}
                </>
              }
            >
              {formatMoney(row.installmentsTotal + row.warmupTotal)}
            </Tooltip>
          )}
        />
        <Table.Column<SettlementRow>
          title="Bikini"
          hidden={!isColumnVisible("bikini")}
          render={(_, row) => {
            if (!row.bikiniSubscriptionTotal && !row.bikiniOneTimeCount) return "-";

            const parts = [
              row.bikiniSubscriptionTotal ? `subskrypcje ${formatMoney(row.bikiniSubscriptionTotal)}` : null,
              row.bikiniOneTimeCount
                ? `${row.bikiniOneTimeCount} × jednorazowe (${formatMoney(row.bikiniOneTimeTotal)})`
                : null,
            ].filter(Boolean);

            return parts.join(" · ");
          }}
        />
        <Table.Column<SettlementRow>
          title="Prowizja jednorazowe"
          hidden={!isColumnVisible("oneTimeCommission")}
          render={(_, row) => `${formatMoney(row.oneTimeCommission)} (${row.config.oneTimePercent}%)`}
        />
        <Table.Column<SettlementRow>
          title="Prowizja raty"
          hidden={!isColumnVisible("installmentsCommission")}
          render={(_, row) => `${formatMoney(row.installmentsCommission)} (${row.config.installmentsPercent}%)`}
        />
        <Table.Column<SettlementRow>
          title="Prowizja bikini"
          hidden={!isColumnVisible("bikiniCommission")}
          render={(_, row) => (
            <Tooltip
              title={`${row.config.bikiniSubscriptionPercent}% pierwszej wpłaty subskrypcji + ${formatMoney(
                row.config.bikiniFlatAmount,
              )} za bikini jednorazowe`}
            >
              {formatMoney(row.bikiniCommission)}
            </Tooltip>
          )}
        />
        <Table.Column<SettlementRow>
          title="Korekta (zwroty)"
          hidden={!isColumnVisible("refunded")}
          render={(_, row) => {
            if (!row.refundedCount) return formatMoney(0);

            const warmupCancelled = row.warmupCancelledCount;
            const refunds = row.refundedCount - warmupCancelled;
            const parts = [
              refunds ? `${refunds} zwrot(ów) do 30 dni` : null,
              warmupCancelled ? `${warmupCancelled} anulowanie w warmupie` : null,
            ].filter(Boolean);

            return (
              <Tooltip title={`${parts.join(" · ")} — cofnięta cała prowizja`}>
                <Typography.Text type="danger">-{formatMoney(row.refundedCommission)}</Typography.Text>
              </Tooltip>
            );
          }}
        />
        <Table.Column<SettlementRow>
          title="Podstawa"
          hidden={!isColumnVisible("base")}
          render={(_, row) => formatMoney(row.config.baseAmount)}
        />
        <Table.Column<SettlementRow>
          title="Do wypłaty"
          hidden={!isColumnVisible("payout")}
          render={(_, row) => <Typography.Text strong>{formatMoney(row.payout)}</Typography.Text>}
        />
        {canManageConfiguration ? (
          <Table.Column<SettlementRow>
            title=""
            render={(_, row) => <Button onClick={() => openConfig(row)}>Stawki</Button>}
          />
        ) : null}
      </Table>

      <Modal
        title={configMember ? `Stawki: ${configMember.firstName} ${configMember.lastName}` : "Stawki"}
        open={Boolean(configMember)}
        onCancel={() => setConfigMember(null)}
        onOk={() => form.submit()}
        confirmLoading={saveConfigMutation.isLoading}
        okText="Zapisz"
      >
        <Form form={form} layout="vertical" onFinish={(values) => saveConfigMutation.mutate(values)}>
          <Form.Item
            name="installmentsPercent"
            label="Prowizja od rat zakontraktowanych (%)"
            rules={[{ required: true, message: "Podaj procent" }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="oneTimePercent"
            label="Prowizja od płatności jednorazowych (%)"
            rules={[{ required: true, message: "Podaj procent" }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="baseAmount" label="Podstawa (PLN)" rules={[{ required: true, message: "Podaj podstawę" }]}>
            <InputNumber min={0} step={100} precision={2} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="bikiniSubscriptionPercent"
            label="Prowizja od subskrypcji bikini (% pierwszej wpłaty)"
            tooltip="Liczona od faktycznej pierwszej wpłaty — także po rabacie"
            rules={[{ required: true, message: "Podaj procent" }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="bikiniFlatAmount"
            label="Stała kwota za bikini jednorazowe (PLN)"
            rules={[{ required: true, message: "Podaj kwotę" }]}
          >
            <InputNumber min={0} step={10} precision={2} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
